import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  sendEmailVerification,
  fetchSignInMethodsForEmail,
  GoogleAuthProvider,
  type OAuthCredential
} from "firebase/auth";
import { auth } from "../firebase";
import { savePendingLink } from "../lib/account-linking";
import { updateUserProfile } from "../lib/callables";

interface Props {
  mode: "login" | "signup";
  onSuccess: () => void;
}

export function AuthForm({ mode, onSuccess }: Props) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === "signup") {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        // Fire-and-log: a failure to send the verification email shouldn't block
        // signup — the /verify-email screen has a resend button for that case.
        sendEmailVerification(cred.user).catch((err) => {
          console.warn("sendEmailVerification failed", err);
        });
        // Persist display name if provided. onUserCreate already ran with
        // displayName=null; this callable patches both Firebase Auth and the
        // /users/{uid} doc to keep them in sync. Fire-and-log: a failure here
        // doesn't undo the signup — the user can fix it later from /account.
        const trimmed = name.trim();
        if (trimmed) {
          updateUserProfile({ displayName: trimmed }).catch((err) => {
            console.warn("updateUserProfile (signup) failed", err);
          });
        }
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
      onSuccess();
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      // Signup path: if the email already belongs to a Google-only account,
      // offer to add the password to it via the /link-account flow.
      if (mode === "signup" && code === "auth/email-already-in-use") {
        try {
          const methods = await fetchSignInMethodsForEmail(auth, email);
          if (methods.includes("google.com") && !methods.includes("password")) {
            savePendingLink({ mode: "add-password", email, pendingPassword: password });
            nav("/link-account");
            return;
          }
        } catch (fetchErr) {
          console.warn("[auth-form] fetchSignInMethodsForEmail failed", fetchErr);
        }
      }
      setError(humanizeAuthError(err, t));
    } finally {
      setBusy(false);
    }
  }

  async function google() {
    setBusy(true);
    setError(null);
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
      onSuccess();
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      // Account-exists conflict: a password account exists for this email; we
      // stash the Google credential and route the user to /link-account where
      // they'll re-enter their password to merge providers.
      if (code === "auth/account-exists-with-different-credential") {
        const cred: OAuthCredential | null = GoogleAuthProvider.credentialFromError(
          err as Parameters<typeof GoogleAuthProvider.credentialFromError>[0]
        );
        const conflictEmail = (err as { customData?: { email?: string } })?.customData?.email;
        if (cred && conflictEmail) {
          savePendingLink({
            mode: "add-google",
            email: conflictEmail,
            googleCredJson: JSON.stringify(cred.toJSON()),
          });
          nav("/link-account");
          return;
        }
      }
      setError(humanizeAuthError(err, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="acx__stack acx__stack--lg">
      <form onSubmit={submit} className="acx__form" noValidate>
        {mode === "signup" && (
          <div className="acx__field">
            <label htmlFor="acx-name" className="acx__label">{t("auth.name")}</label>
            <input
              id="acx-name"
              className="acx__input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("auth.namePlaceholder")}
              autoComplete="name"
              maxLength={80}
              disabled={busy}
            />
          </div>
        )}
        <div className="acx__field">
          <label htmlFor="acx-email" className="acx__label">{t("auth.email")}</label>
          <input
            id="acx-email"
            className="acx__input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("auth.emailPlaceholder")}
            autoComplete="email"
            disabled={busy}
          />
        </div>
        <div className="acx__field">
          <label htmlFor="acx-pw" className="acx__label">{t("auth.password")}</label>
          <input
            id="acx-pw"
            className="acx__input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={mode === "signup" ? t("auth.passwordPlaceholderSignup") : t("auth.passwordPlaceholderLogin")}
            autoComplete={mode === "signup" ? "new-password" : "current-password"}
            disabled={busy}
          />
        </div>
        <button type="submit" className="acx__btn acx__btn--primary acx__btn--full" disabled={busy}>
          {busy ? t("auth.working") : mode === "signup" ? t("auth.createAccount") : t("auth.signIn")}
        </button>
        {mode === "login" && (
          <p className="acx__auth-footer" style={{ marginTop: "0.75rem", marginBottom: 0 }}>
            <Link to="/forgot-password">{t("auth.forgotPassword")}</Link>
          </p>
        )}
      </form>

      <div className="acx__divider">{t("common.or")}</div>

      <button
        type="button"
        className="acx__btn acx__btn--ghost acx__btn--full"
        onClick={google}
        disabled={busy}
      >
        <GoogleGlyph />
        {t("auth.continueWithGoogle")}
      </button>

      {error && <div className="acx__alert acx__alert--danger" role="alert">{error}</div>}
    </div>
  );
}

type TFunc = (key: string) => string;

function humanizeAuthError(err: unknown, t: TFunc): string {
  const code = (err as { code?: string })?.code;
  // App Check no pudo attestar el navegador. Verificado en dev: cuando el
  // token falla, el SDK rechaza la operación de Auth entera — así que esto no
  // es cosmético, el usuario queda afuera del login. Pasa aunque el
  // enforcement esté apagado, y la causa típica es un bloqueador cortando
  // google.com/recaptcha. Sin este case, el `default:` de abajo le mostraría
  // el mensaje crudo del SDK en inglés.
  if (code?.startsWith("appCheck/")) return t("auth.errors.appCheckFailed");
  switch (code) {
    case "auth/invalid-email": return t("auth.errors.invalidEmail");
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return t("auth.errors.wrongCredentials");
    case "auth/email-already-in-use": return t("auth.errors.emailInUse");
    case "auth/weak-password": return t("auth.errors.weakPassword");
    case "auth/popup-closed-by-user": return t("auth.errors.popupClosed");
    case "auth/network-request-failed": return t("auth.errors.networkFailed");
    case "auth/too-many-requests": return t("auth.errors.tooManyAttempts");
    case "auth/account-exists-with-different-credential":
      return t("auth.errors.accountExistsDifferentCredential");
    default:
      return err instanceof Error ? err.message : t("auth.errors.generic");
  }
}

function GoogleGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden>
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.166 6.656 3.58 9 3.58z"/>
    </svg>
  );
}
